import { createSelector } from 'reselect';
import { abstractsSelector } from '../selectors';
import { getAbstractsDevice } from 'containers/Ezlo/EzloMeshbot/selectors';

/**
 * Memoized selector that merges abstract devices from two sources:
 * - abstractsSelector (state.main.abstractsList.abstracts) - contains device details
 * - getAbstractsDevice (state.meshBot.abstractsDevice) - contains device meta information
 *
 * Devices are merged by UUID. Meta properties override details properties if both exist.
 * @param {Object} state - Redux state.
 * @returns {Array<Object>} Array of abstract devices with combined details and meta.
 * @example
 * // In component:
 * const abstracts = useSelector(abstractsWithMetaAndDetailsSelector);
 * // Returns devices with both details (capabilities, etc.) and meta (parent_room_name, etc.)
 */
export const abstractsWithMetaAndDetailsSelector = createSelector(
    [abstractsSelector, getAbstractsDevice],
    (abstractsListWithDetails = [], abstractsListWithMeta = []) => {
        const abstractsByUuid = abstractsListWithDetails?.reduce((acc, current) => {
            if (current.uuid) {
                acc[current.uuid] = current;
            }

            return acc;
        }, {});

        abstractsListWithMeta?.forEach((abstract) => {
            if (abstractsByUuid[abstract.uuid]) {
                abstractsByUuid[abstract.uuid] = {
                    ...abstractsByUuid[abstract.uuid],
                    ...abstract,
                };
            } else {
                abstractsByUuid[abstract.uuid] = {
                    ...abstract,
                };
            }
        });

        return Object.values(abstractsByUuid);
    },
);
